import React, {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import { Loading, Toast } from "@xp-design/mobile";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { getCurrentUid } from "@/api/common";
import { orderDetail } from "@/pages/par-marketing/detailPage/ajax";
import { ApiResponse, Order } from "@/pages/par-marketing/detailPage/type";
import PopupCom from "@/component/popupCom";
import { goToNativePage } from "@/utils/common";
import imageUrl from "@/assets/detailPage/infoRight@2x.png";
import copyImage from "@/assets/copy@1x.png";
import styles from "./index.module.less";

interface ActivityValueDetailPopupProps {
  moduleJsonData?: any;
  activityInfo?: any;
}

export interface ActivityValueDetailPopupRef {
  show: (params: {
    activityCode: string;
    hdFontVal: string;
    selfActivityValue?: string;
    isOver?: string;
    pageCode?: string;
  }) => void;
}

const ActivityValueDetailPopup = forwardRef<
  ActivityValueDetailPopupRef,
  ActivityValueDetailPopupProps
>(({ moduleJsonData, activityInfo }, ref) => {
  const popupRef = useRef<any>(null);
  const [maskShow, setMaskShow] = useState(false);
  const [list, setList] = useState<Order[]>([]);
  const [selfActivityValue, setSelfActivityValue] = useState<string>("");
  const [hdFontVal, setHdFontVal] = useState<string>("");
  const [currentParams, setCurrentParams] = useState<any>({});

  // 获取订单明细数据
  const getData = async (activityCode: string) => {
    try {
      Loading.show("加载中...");
      const uid = await getCurrentUid();
      const res = (await orderDetail({
        uid,
        activityCode,
        pageNo: 1,
        pageSize: -1,
      })) as ApiResponse;

      console.log("%c ActivityValueDetailPopup getData", "color:#93c0a4", res);

      setList(res.records[0]?.orderList || []);
      setSelfActivityValue(res.records[0]?.selfActivityValue || "");
    } catch (error) {
      console.error("获取活动值明细失败:", error);
      Toast.show({
        content: "获取数据失败，请重试",
      });
    } finally {
      Loading.hide();
    }
  };

  // 复制订单号
  const handleCopy = () => {
    Toast.show({
      content: "已复制到剪贴板",
    });
  };

  // 查看订单详情
  const seeDetail = (orderNo: string) => {
    goToNativePage({
      mini: `/charging/pages/order-detail/index?orderNo=${orderNo}`,
      app: `xiaopeng://charging/manage?orderNo=${orderNo}`,
      browser: `https://app.xiaopeng.com/activity/openurl?url=${encodeURIComponent(`xiaopeng://charging/manage?orderNo=${orderNo}`)}`,
    });
  };

  // 显示弹窗
  const showPopup = (params: {
    activityCode: string;
    hdFontVal: string;
    selfActivityValue?: string;
    isOver?: string;
    pageCode?: string;
  }) => {
    // 保存参数
    setCurrentParams(params);
    setHdFontVal(params.hdFontVal);
    if (params.selfActivityValue) {
      setSelfActivityValue(params.selfActivityValue);
    }

    // 重置状态
    setList([]);

    setMaskShow(true);
    popupRef.current?.onOpen();

    // 加载数据
    getData(params.activityCode);
  };

  // 关闭回调
  const onCloseCallBack = async () => {
    setTimeout(() => {
      setMaskShow(false);
      // 清理数据
      setList([]);
      setSelfActivityValue("");
      setHdFontVal("");
      setCurrentParams({});
    }, 500);
  };

  useImperativeHandle(ref, () => ({
    show: showPopup,
  }));

  // 获取弹窗背景图 - 使用默认背景或者从moduleJsonData获取
  const getPopupBackUrl = () => {
    // 可以根据实际需要从moduleJsonData中获取背景图配置
    return moduleJsonData?.visual?.prizeSetting?.[2]?.content?.[0]?.val || "";
  };

  return (
    <>
      <PopupCom
        ref={popupRef}
        height="80%"
        bacUrl={getPopupBackUrl()}
        onCloseCallBack={onCloseCallBack}
        title={`${hdFontVal}明细`}
        children={
          <div className={styles.activityDetailContent}>
            {/* 总值信息框 */}
            <div className={styles.infoBox}>
              <div>
                <div className={styles.label}>总{hdFontVal}</div>
                <div className={styles.value}>{selfActivityValue}</div>
              </div>
              <img src={imageUrl} alt="找不到图片" />
            </div>

            {/* 订单列表 */}
            <div className={styles.listBox}>
              {list.map((item, i) => (
                <div className={styles.listInfoBox} key={`listInfoBox${i}`}>
                  <div className={styles.listInfoBoxTitle}>
                    <div className={styles.title}>{item.electricityName}</div>
                    <div className={styles.activityValue}>
                      +{item.activityValue}
                    </div>
                  </div>
                  <div className={styles.listInfoBoxOrderNo}>
                    <div className={styles.orderNo}>订单号：{item.orderNo}</div>
                    <CopyToClipboard text={item.orderNo} onCopy={handleCopy}>
                      <img src={copyImage} alt="复制" />
                    </CopyToClipboard>
                  </div>
                  <div className={styles.listInfoBoxTime}>
                    <div className={styles.time}>
                      获得时间：{item.createTime}
                    </div>
                    <div className={styles.btnWrapper}>
                      <div
                        className={styles.btn}
                        onClick={() => seeDetail(item.orderNo)}
                      >
                        查看详情
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {list.length === 0 && (
                <div className={styles.noData}>
                  <div>暂无明细记录</div>
                </div>
              )}
            </div>
          </div>
        }
      />

      {maskShow && <div className={styles.prizeMask}></div>}
    </>
  );
});

ActivityValueDetailPopup.displayName = "ActivityValueDetailPopup";

export default ActivityValueDetailPopup;
